import { useEffect, useMemo, useRef, useState } from 'react';
import { ListPage } from '../../components/list';
import { Button, Input, Modal, useToast } from '../../components/primitives';
import {
  useVehicles,
  useCreateVehicle,
  useUpdateVehicle,
  useDeleteVehicle,
  useLogbookMonths,
  useLogbookMonth,
  useCreateLogbookMonth,
  useUpdateLogbookMonth,
  useDeleteLogbookMonth,
} from '../../api/hooks';
import { api, apiFetch, ApiError } from '../../lib/api';
import type { Vehicle, LogbookMonth, LogbookEntryIn } from '../../api/schemas';
import styles from './KnihaJizdPage.module.css';

const MONTH_NAMES = [
  '', 'leden', 'únor', 'březen', 'duben', 'květen', 'červen',
  'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec',
];
const MONTH_SHORT = [
  '', 'led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro',
];
const UCELY = [
  'služebně', 'soukromě', 'dohled na stavbě', 'obchodní jednání',
  'nákup materiálu', 'kontrolní den', 'servis vozidla',
];

const FIELDS = [
  'cesta', 'ucel', 'km_sluzebni', 'km_soukrome',
  'nat_benzin', 'nat_nafta', 'nat_olej', 'podpis', 'pozn',
] as const;
type Field = (typeof FIELDS)[number];
type Draft = Record<Field, string>;

function emptyDraft(): Draft {
  return {
    cesta: '', ucel: '', km_sluzebni: '', km_soukrome: '',
    nat_benzin: '', nat_nafta: '', nat_olej: '', podpis: '', pozn: '',
  };
}

function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function fmtKm(n: number): string {
  return num(n).toLocaleString('cs-CZ', { maximumFractionDigits: 1 });
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ─── PDF (port z legacy frontendu) ───────────────────────────────────────────

function buildMonthHtml(month: LogbookMonth, vehicle: Vehicle): string {
  const days = daysInMonth(month.year, month.month);
  let prev = num(month.pocatecni_km);
  const byDay: Record<number, LogbookMonth['entries'][number]> = {};
  (month.entries || []).forEach((e) => { byDay[e.den] = e; });

  let sumSluz = 0, sumSou = 0, sumBenz = 0, sumNaft = 0, sumOlej = 0;
  let rows = '';
  for (let d = 1; d <= days; d++) {
    const e = byDay[d];
    const sluz = num(e?.km_sluzebni), sou = num(e?.km_soukrome);
    const driven = sluz + sou;
    const after = prev + driven;
    sumSluz += sluz; sumSou += sou;
    sumBenz += num(e?.nat_benzin); sumNaft += num(e?.nat_nafta); sumOlej += num(e?.nat_olej);
    const f = (n: number) => (n ? fmtKm(n) : '');
    rows +=
      '<tr>' +
      `<td class="num">${d}.</td>` +
      `<td>${esc(e?.cesta || '')}</td>` +
      `<td>${esc(e?.ucel || '')}</td>` +
      `<td class="num">${f(sluz)}</td>` +
      `<td class="num">${f(sou)}</td>` +
      `<td class="num">${driven > 0 ? fmtKm(prev) : ''}</td>` +
      `<td class="num bold">${driven > 0 ? fmtKm(after) : ''}</td>` +
      `<td class="num">${f(num(e?.nat_benzin))}</td>` +
      `<td class="num">${f(num(e?.nat_nafta))}</td>` +
      `<td class="num">${f(num(e?.nat_olej))}</td>` +
      `<td>${esc(e?.podpis || '')}</td>` +
      `<td>${esc(e?.pozn || '')}</td>` +
      '</tr>';
    if (driven > 0) prev = after;
  }
  const totalKm = sumSluz + sumSou;
  const tank = sumBenz + sumNaft;
  const avg = totalKm > 0 && tank > 0 ? (tank / totalKm * 100).toFixed(2) : '—';
  const ridic = month.ridic_override || vehicle.ridic || '';

  return (
    '<section class="kj-page">' +
      '<div class="kj-header">' +
        '<table class="kj-meta">' +
          `<tr><td><strong>Řidič:</strong> ${esc(ridic)}</td>` +
          `<td><strong>Vozidlo:</strong> ${esc(vehicle.typ || vehicle.nazev)}</td></tr>` +
          `<tr><td><strong>SPZ:</strong> ${esc(vehicle.spz)}</td>` +
          `<td><strong>Č. tankovací karty:</strong> ${esc(vehicle.karta)}</td></tr>` +
          `<tr><td colspan="2"><strong>Měsíc/rok:</strong> ${String(month.month).padStart(2, '0')}/${month.year}` +
            ` (${MONTH_NAMES[month.month]} ${month.year})</td></tr>` +
        '</table>' +
        '<table class="kj-summary">' +
          '<tr><th colspan="3">Ujeté km celkem</th><th colspan="3">Spotřebované PHM</th><th>Průměrná spotřeba</th></tr>' +
          '<tr><td><em>služební</em></td><td><em>soukromé</em></td><td><em>celkem</em></td>' +
          '<td><em>benzin</em></td><td><em>nafta</em></td><td><em>olej</em></td>' +
          `<td rowspan="2" class="big">${avg} l/100km</td></tr>` +
          `<tr><td class="num">${fmtKm(sumSluz)}</td><td class="num">${fmtKm(sumSou)}</td>` +
          `<td class="num bold">${fmtKm(totalKm)}</td><td class="num">${fmtKm(sumBenz)}</td>` +
          `<td class="num">${fmtKm(sumNaft)}</td><td class="num">${fmtKm(sumOlej)}</td></tr>` +
          (vehicle.spotreba_tp
            ? `<tr><td colspan="7" class="tp-note">Spotřeba dle velkého TP: ${fmtKm(vehicle.spotreba_tp)} l/100km</td></tr>`
            : '') +
        '</table>' +
      '</div>' +
      '<table class="kj-rows"><thead><tr>' +
        '<th rowspan="2">datum</th><th rowspan="2">cíl cesty nebo jiná činnost</th>' +
        '<th rowspan="2">účel jízdy</th><th colspan="2">ujeto km</th>' +
        '<th colspan="2">stav tachometru</th><th colspan="3">Doplnění (litry)</th>' +
        '<th rowspan="2">Podpis řidiče</th><th rowspan="2">Pozn.</th>' +
      '</tr><tr><th>služ.</th><th>soukr.</th><th>před</th><th>po</th>' +
        '<th>benz.</th><th>nafty</th><th>oleje</th></tr></thead>' +
        `<tbody>${rows}</tbody></table>` +
      '<div class="kj-foot">podpis řidiče: ___________________________</div>' +
    '</section>'
  );
}

function pdfCss(): string {
  return (
    '@page { size: A4 portrait; margin: 8mm 6mm; }' +
    'body { font-family: Arial, sans-serif; font-size: 8pt; color: #000; margin: 0; }' +
    '.kj-page { page-break-after: always; }' +
    '.kj-page:last-child { page-break-after: auto; }' +
    '.kj-header { margin-bottom: 4mm; }' +
    '.kj-meta { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 3mm; }' +
    '.kj-meta td { padding: 1.5mm 2mm; border: 0.2mm solid #444; }' +
    '.kj-summary { width: 100%; border-collapse: collapse; font-size: 8pt; margin-bottom: 3mm; }' +
    '.kj-summary th { background: #eef5fb; border: 0.2mm solid #444; padding: 1.2mm; font-weight: 700; }' +
    '.kj-summary td { border: 0.2mm solid #444; padding: 1.2mm; text-align: center; }' +
    '.kj-summary em { color: #555; font-style: normal; font-size: 7pt; }' +
    '.kj-summary .big { font-size: 11pt; font-weight: 700; background: #fff7e6; }' +
    '.kj-summary .tp-note { font-size: 7pt; color: #666; text-align: left; padding: 1mm 2mm; background: #fafafa; }' +
    '.kj-rows { width: 100%; border-collapse: collapse; font-size: 7.5pt; }' +
    '.kj-rows th { background: #f0f0f0; border: 0.2mm solid #444; padding: 1mm; font-weight: 700; }' +
    '.kj-rows td { border: 0.15mm solid #999; padding: 0.8mm 1.2mm; height: 4.2mm; vertical-align: middle; }' +
    '.kj-rows td.num { text-align: right; font-variant-numeric: tabular-nums; }' +
    '.kj-rows td.bold { font-weight: 700; background: #f7faff; }' +
    '.kj-foot { margin-top: 6mm; font-size: 9pt; }'
  );
}

async function downloadPdf(sections: string, filename: string): Promise<void> {
  const html =
    `<!DOCTYPE html><html lang="cs"><head><meta charset="utf-8"><style>${pdfCss()}</style></head>` +
    `<body>${sections}</body></html>`;
  const blob = (await apiFetch('/pdf/generate', { method: 'POST', body: { html, filename } })) as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Vehicle modal ───────────────────────────────────────────────────────────

interface VehicleForm {
  nazev: string; spz: string; typ: string; karta: string; spotreba: string; ridic: string;
}
const emptyVehicleForm: VehicleForm = { nazev: '', spz: '', typ: '', karta: '', spotreba: '', ridic: '' };

// ─── Page ────────────────────────────────────────────────────────────────────

export function KnihaJizdPage() {
  const toast = useToast();
  const currentYear = new Date().getFullYear();

  const { data: vehicles } = useVehicles();
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [year, setYear] = useState(currentYear);
  const [openMonth, setOpenMonth] = useState<number | null>(null);

  const activeVehicle = useMemo(
    () => vehicles?.find((v) => v.id === vehicleId) ?? null,
    [vehicles, vehicleId],
  );

  const { data: months } = useLogbookMonths(vehicleId);
  const monthQuery = useLogbookMonth(vehicleId, year, openMonth);

  const createVehicle = useCreateVehicle();
  const updateVehicle = useUpdateVehicle();
  const deleteVehicle = useDeleteVehicle();
  const createMonth = useCreateLogbookMonth(vehicleId);
  const updateMonth = useUpdateLogbookMonth(vehicleId);
  const deleteMonth = useDeleteLogbookMonth(vehicleId);

  // Editovatelný stav otevřeného měsíce
  const [ridic, setRidic] = useState('');
  const [pocatecniKm, setPocatecniKm] = useState('0');
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [dirty, setDirty] = useState(false);
  const seededRef = useRef<number | null>(null);

  // Modaly
  const [vehicleModal, setVehicleModal] = useState<'new' | 'edit' | null>(null);
  const [vehicleForm, setVehicleForm] = useState<VehicleForm>(emptyVehicleForm);
  const [addMonthOpen, setAddMonthOpen] = useState(false);
  const [amYear, setAmYear] = useState(currentYear);
  const [amMonth, setAmMonth] = useState(1);
  const [amPocatecni, setAmPocatecni] = useState('');

  // Auto-výběr vozidla (poslední z localStorage, jinak první)
  useEffect(() => {
    if (vehicleId != null || !vehicles?.length) return;
    const last = localStorage.getItem('kj_last_vehicle');
    const picked = (last && vehicles.find((v) => String(v.id) === last)) || vehicles[0];
    setVehicleId(picked.id);
  }, [vehicles, vehicleId]);

  // Načtení dat měsíce do editovatelného stavu
  useEffect(() => {
    const m = monthQuery.data;
    if (!m || seededRef.current === m.id) return;
    seededRef.current = m.id;
    setRidic(m.ridic_override || activeVehicle?.ridic || '');
    setPocatecniKm(String(m.pocatecni_km ?? 0));
    const d: Record<number, Draft> = {};
    m.entries.forEach((e) => {
      d[e.den] = {
        cesta: e.cesta || '', ucel: e.ucel || '',
        km_sluzebni: e.km_sluzebni ? String(e.km_sluzebni) : '',
        km_soukrome: e.km_soukrome ? String(e.km_soukrome) : '',
        nat_benzin: e.nat_benzin ? String(e.nat_benzin) : '',
        nat_nafta: e.nat_nafta ? String(e.nat_nafta) : '',
        nat_olej: e.nat_olej ? String(e.nat_olej) : '',
        podpis: e.podpis || '', pozn: e.pozn || '',
      };
    });
    setDrafts(d);
    setDirty(false);
  }, [monthQuery.data, activeVehicle]);

  // Přepočet (stav tachometru, součty, průměr)
  const calc = useMemo(() => {
    const days = openMonth ? daysInMonth(year, openMonth) : 0;
    let prev = num(pocatecniKm);
    let sumSluz = 0, sumSou = 0, sumBenz = 0, sumNaft = 0;
    const rows: { den: number; pred: number; po: number | null }[] = [];
    for (let d = 1; d <= days; d++) {
      const dr = drafts[d];
      const sluz = num(dr?.km_sluzebni), sou = num(dr?.km_soukrome);
      sumBenz += num(dr?.nat_benzin); sumNaft += num(dr?.nat_nafta);
      const driven = sluz + sou;
      const after = prev + driven;
      rows.push({ den: d, pred: prev, po: driven > 0 ? after : null });
      sumSluz += sluz; sumSou += sou;
      if (driven > 0) prev = after;
    }
    const totalKm = sumSluz + sumSou;
    const tank = sumBenz + sumNaft;
    return {
      rows, sumSluz, sumSou, konec: prev, tank,
      avg: totalKm > 0 && tank > 0 ? tank / totalKm * 100 : null,
    };
  }, [drafts, pocatecniKm, openMonth, year]);

  function setField(day: number, field: Field, value: string) {
    setDrafts((prev) => ({ ...prev, [day]: { ...(prev[day] ?? emptyDraft()), [field]: value } }));
    setDirty(true);
  }

  async function save(silent = false): Promise<void> {
    if (!vehicleId || !openMonth) return;
    const entries: LogbookEntryIn[] = [];
    const days = daysInMonth(year, openMonth);
    for (let d = 1; d <= days; d++) {
      const dr = drafts[d];
      if (!dr) continue;
      const has = dr.cesta || dr.ucel || dr.km_sluzebni || dr.km_soukrome ||
        dr.nat_benzin || dr.nat_nafta || dr.nat_olej || dr.podpis || dr.pozn;
      if (!has) continue;
      entries.push({
        den: d, cesta: dr.cesta, ucel: dr.ucel,
        km_sluzebni: num(dr.km_sluzebni), km_soukrome: num(dr.km_soukrome),
        nat_benzin: num(dr.nat_benzin), nat_nafta: num(dr.nat_nafta), nat_olej: num(dr.nat_olej),
        podpis: dr.podpis, pozn: dr.pozn,
      });
    }
    try {
      await updateMonth.mutateAsync({
        year, month: openMonth,
        payload: { pocatecni_km: num(pocatecniKm), ridic_override: ridic, entries },
      });
      setDirty(false);
      if (!silent) toast.success('Uloženo');
    } catch {
      toast.error('Uložení selhalo');
    }
  }

  // Autosave po 4 s nečinnosti
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => { void saveRef.current(true); }, 4000);
    return () => clearTimeout(t);
  }, [dirty, drafts, pocatecniKm, ridic]);

  async function selectVehicle(id: number | null) {
    if (dirty) await save(true);
    setVehicleId(id);
    setOpenMonth(null);
    seededRef.current = null;
    if (id) localStorage.setItem('kj_last_vehicle', String(id));
    else localStorage.removeItem('kj_last_vehicle');
  }

  async function changeYear(y: number) {
    if (dirty) await save(true);
    setYear(y);
    setOpenMonth(null);
    seededRef.current = null;
  }

  async function openMonthAt(m: number) {
    if (dirty) await save(true);
    seededRef.current = null;
    setOpenMonth(m);
  }

  // ── Vehicle modal akce ──
  function openVehicleModal(mode: 'new' | 'edit') {
    if (mode === 'edit') {
      if (!activeVehicle) return;
      setVehicleForm({
        nazev: activeVehicle.nazev || '', spz: activeVehicle.spz || '',
        typ: activeVehicle.typ || '', karta: activeVehicle.karta || '',
        spotreba: activeVehicle.spotreba_tp ? String(activeVehicle.spotreba_tp) : '',
        ridic: activeVehicle.ridic || '',
      });
    } else {
      setVehicleForm(emptyVehicleForm);
    }
    setVehicleModal(mode);
  }

  async function saveVehicle() {
    const nazev = vehicleForm.nazev.trim();
    if (!nazev) { toast.error('Vyplňte název vozidla'); return; }
    const payload = {
      nazev, spz: vehicleForm.spz.trim(), typ: vehicleForm.typ.trim(),
      karta: vehicleForm.karta.trim(), ridic: vehicleForm.ridic.trim(),
      spotreba_tp: num(vehicleForm.spotreba),
    };
    try {
      if (vehicleModal === 'edit' && activeVehicle) {
        await updateVehicle.mutateAsync({ id: activeVehicle.id, payload });
      } else {
        const created = await createVehicle.mutateAsync(payload);
        await selectVehicle(created.id);
      }
      setVehicleModal(null);
      toast.success('Uloženo');
    } catch {
      toast.error('Uložení vozidla selhalo');
    }
  }

  async function removeVehicle() {
    if (!activeVehicle) return;
    if (!window.confirm(`Smazat vozidlo „${activeVehicle.nazev}" včetně všech měsíců a jízd? Tato akce je nevratná.`)) return;
    try {
      await deleteVehicle.mutateAsync(activeVehicle.id);
      setVehicleModal(null);
      localStorage.removeItem('kj_last_vehicle');
      setVehicleId(null);
      setOpenMonth(null);
      seededRef.current = null;
    } catch {
      toast.error('Smazání selhalo');
    }
  }

  // ── Přidat měsíc ──
  function openAddMonth(preset?: number) {
    if (!vehicleId) return;
    setAmYear(year);
    let pick = preset;
    if (!pick) {
      const have = new Set((months ?? []).filter((m) => m.year === year).map((m) => m.month));
      const cur = new Date().getMonth() + 1;
      pick = null as unknown as number;
      for (let i = 0; i < 12 && !pick; i++) {
        const m = ((cur - 1 + i) % 12) + 1;
        if (!have.has(m)) pick = m;
      }
      if (!pick) pick = cur;
    }
    setAmMonth(pick);
    setAmPocatecni('');
    setAddMonthOpen(true);
  }

  async function confirmAddMonth() {
    if (!vehicleId) return;
    const payload: { year: number; month: number; pocatecni_km?: number } = { year: amYear, month: amMonth };
    if (amPocatecni.trim() !== '') payload.pocatecni_km = num(amPocatecni);
    try {
      await createMonth.mutateAsync(payload);
      setAddMonthOpen(false);
      if (amYear !== year) setYear(amYear);
      seededRef.current = null;
      setOpenMonth(amMonth);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setAddMonthOpen(false);
        if (window.confirm(`${MONTH_NAMES[amMonth]} ${amYear} už existuje. Otevřít?`)) {
          if (amYear !== year) setYear(amYear);
          seededRef.current = null;
          setOpenMonth(amMonth);
        }
        return;
      }
      toast.error('Vytvoření měsíce selhalo');
    }
  }

  async function removeMonth() {
    if (!openMonth) return;
    if (!window.confirm(`Smazat měsíc ${MONTH_NAMES[openMonth]} ${year} včetně všech zápisů?`)) return;
    try {
      await deleteMonth.mutateAsync({ year, month: openMonth });
      setOpenMonth(null);
      seededRef.current = null;
    } catch {
      toast.error('Smazání selhalo');
    }
  }

  // ── PDF ──
  async function printMonth() {
    if (!vehicleId || !openMonth || !activeVehicle) return;
    if (dirty) await save(true);
    try {
      const m = await api.get<LogbookMonth>(`/vehicles/${vehicleId}/months/${year}/${openMonth}`);
      const fname = `kniha-jizd_${(activeVehicle.spz || activeVehicle.nazev).replace(/\W+/g, '_')}_${String(openMonth).padStart(2, '0')}-${year}.pdf`;
      await downloadPdf(buildMonthHtml(m, activeVehicle), fname);
    } catch {
      toast.error('PDF se nepodařilo vygenerovat');
    }
  }

  async function printYear() {
    if (!vehicleId || !activeVehicle) return;
    if (dirty) await save(true);
    try {
      const yearMonths = await api.get<LogbookMonth[]>(`/vehicles/${vehicleId}/year/${year}`);
      if (!yearMonths.length) { toast.info('V tomto roce nejsou žádné měsíce'); return; }
      const html = yearMonths.map((m) => buildMonthHtml(m, activeVehicle)).join('\n');
      const fname = `kniha-jizd_${(activeVehicle.spz || activeVehicle.nazev).replace(/\W+/g, '_')}_rok-${year}.pdf`;
      await downloadPdf(html, fname);
    } catch {
      toast.error('PDF se nepodařilo vygenerovat');
    }
  }

  // ── Render ──
  const monthsInYear = (months ?? []).filter((m) => m.year === year);
  const haveMonths = new Set(monthsInYear.map((m) => m.month));
  const yearOptions: number[] = [];
  for (let y = currentYear + 1; y >= currentYear - 8; y--) yearOptions.push(y);

  const actions = activeVehicle ? (
    <div className={styles.actions}>
      {openMonth && (
        <Button variant={dirty ? 'primary' : 'secondary'} size="sm" onClick={() => save()}>
          {dirty ? 'Uložit změny' : 'Uloženo'}
        </Button>
      )}
      {openMonth && (
        <Button variant="secondary" size="sm" onClick={printMonth}>PDF měsíc</Button>
      )}
      {monthsInYear.length > 0 && (
        <Button variant="secondary" size="sm" onClick={printYear}>PDF rok</Button>
      )}
      {openMonth && (
        <Button variant="danger" size="sm" onClick={removeMonth}>Smazat měsíc</Button>
      )}
    </div>
  ) : null;

  return (
    <ListPage
      breadcrumb={['Obchod', 'Kniha jízd']}
      title="Kniha jízd"
      subtitle="Evidence služebních a soukromých jízd vozidel s měsíčním rozpisem a tiskem do PDF."
      status={dirty ? 'draft' : undefined}
      statusText={dirty ? 'Neuložené změny' : undefined}
      actions={actions}
    >
      <div className={styles.toolbar}>
        <span className={styles.tLabel}>Vozidlo</span>
        <select
          className={styles.select}
          value={vehicleId ?? ''}
          onChange={(e) => void selectVehicle(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">— vyberte vozidlo —</option>
          {(vehicles ?? []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.nazev}{v.spz ? ` (${v.spz})` : ''}
            </option>
          ))}
        </select>
        <Button variant="primary" size="sm" onClick={() => openVehicleModal('new')}>+ Vozidlo</Button>
        {activeVehicle && (
          <Button variant="ghost" size="sm" onClick={() => openVehicleModal('edit')}>Upravit</Button>
        )}

        <span className={styles.tLabel} style={{ marginLeft: 12 }}>Rok</span>
        <select
          className={styles.select}
          style={{ minWidth: 90 }}
          value={year}
          onChange={(e) => void changeYear(Number(e.target.value))}
        >
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {activeVehicle && (
          <Button variant="secondary" size="sm" onClick={() => openAddMonth()}>+ Měsíc</Button>
        )}
      </div>

      {!activeVehicle && (
        <div className={styles.empty}>
          Vyberte vozidlo, nebo nové vytvořte tlačítkem <strong>+ Vozidlo</strong>.
        </div>
      )}

      {activeVehicle && (
        <div className={styles.monthBar}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const exists = haveMonths.has(m);
            const isActive = openMonth === m;
            const summary = monthsInYear.find((x) => x.month === m);
            const title = exists && summary
              ? `Služ.: ${fmtKm(summary.km_sluzebni_total)} · Soukr.: ${fmtKm(summary.km_soukrome_total)} km`
              : 'Zatím nezaložen';
            return (
              <button
                key={m}
                type="button"
                title={title}
                className={[
                  styles.monthChip,
                  exists ? styles.monthExists : '',
                  isActive ? styles.monthActive : '',
                ].join(' ')}
                onClick={() => (exists ? void openMonthAt(m) : openAddMonth(m))}
              >
                {MONTH_SHORT[m]} {year}
              </button>
            );
          })}
        </div>
      )}

      {activeVehicle && !openMonth && (
        <div className={styles.empty}>
          Vyberte existující měsíc, nebo přidejte nový tlačítkem <strong>+ Měsíc</strong>.
        </div>
      )}

      {activeVehicle && openMonth && (
        <>
          <div className={styles.metaGrid}>
            <label className={styles.metaItem}>
              <span className={styles.metaLabel}>Řidič</span>
              <input
                className={styles.metaInput}
                value={ridic}
                placeholder="Řidič tento měsíc"
                onChange={(e) => { setRidic(e.target.value); setDirty(true); }}
              />
              <span className={styles.metaHint}>Prázdné = z profilu vozidla</span>
            </label>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Vozidlo</span>
              <div className={styles.metaRO}>{activeVehicle.typ || activeVehicle.nazev || '—'}</div>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>SPZ</span>
              <div className={styles.metaRO}>{activeVehicle.spz || '—'}</div>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Č. tankovací karty</span>
              <div className={styles.metaRO}>{activeVehicle.karta || '—'}</div>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Měsíc / rok</span>
              <div className={styles.metaRO}>
                {String(openMonth).padStart(2, '0')} / {year} ({MONTH_NAMES[openMonth]})
              </div>
            </div>
          </div>

          <div className={styles.summaryGrid}>
            <label className={styles.sumItem}>
              <span className={styles.sumLabel}>Počáteční stav (km)</span>
              <input
                className={styles.sumInput}
                type="number"
                value={pocatecniKm}
                onChange={(e) => { setPocatecniKm(e.target.value); setDirty(true); }}
              />
            </label>
            <div className={styles.sumItem}>
              <span className={styles.sumLabel}>Služební km</span>
              <div className={styles.sumVal}>{fmtKm(calc.sumSluz)}</div>
            </div>
            <div className={styles.sumItem}>
              <span className={styles.sumLabel}>Soukromé km</span>
              <div className={styles.sumVal}>{fmtKm(calc.sumSou)}</div>
            </div>
            <div className={styles.sumItem}>
              <span className={styles.sumLabel}>Konečný stav (km)</span>
              <div className={styles.sumVal}>{fmtKm(calc.konec)}</div>
            </div>
            <div className={styles.sumItem}>
              <span className={styles.sumLabel}>Natankováno (l)</span>
              <div className={styles.sumVal}>{fmtKm(calc.tank)} l</div>
              <span className={styles.metaHint}>benzin + nafta</span>
            </div>
            <div className={styles.sumItem}>
              <span className={styles.sumLabel}>Průměr l/100km</span>
              <div className={styles.sumVal}>{calc.avg !== null ? `${calc.avg.toFixed(2)} l` : '—'}</div>
              <span className={styles.metaHint}>
                dle TP: {activeVehicle.spotreba_tp ? `${fmtKm(activeVehicle.spotreba_tp)} l/100km` : '—'}
              </span>
            </div>
          </div>

          <datalist id="kj-ucely">
            {UCELY.map((u) => <option key={u} value={u} />)}
          </datalist>

          <div className={styles.tableWrap}>
            <table className={styles.kjTable}>
              <thead>
                <tr>
                  <th className={styles.colDay}>Den</th>
                  <th className={styles.left}>Cíl cesty / činnost</th>
                  <th className={`${styles.left} ${styles.colUcel}`}>Účel</th>
                  <th className={`${styles.right} ${styles.colKm}`}>služ.</th>
                  <th className={`${styles.right} ${styles.colKm}`}>soukr.</th>
                  <th className={`${styles.right} ${styles.tachoCol}`}>před</th>
                  <th className={`${styles.right} ${styles.tachoCol}`}>po</th>
                  <th className={`${styles.right} ${styles.colNat}`} title="Benzín (l)">B</th>
                  <th className={`${styles.right} ${styles.colNat}`} title="Nafta (l)">N</th>
                  <th className={`${styles.right} ${styles.colNat}`} title="Olej (l)">O</th>
                  <th className={`${styles.left} ${styles.colPodpis}`}>Podpis</th>
                  <th className={`${styles.left} ${styles.colPozn}`}>Pozn.</th>
                </tr>
              </thead>
              <tbody>
                {calc.rows.map((r) => {
                  const dr = drafts[r.den] ?? emptyDraft();
                  const numInput = (f: Field) => (
                    <input
                      className={`${styles.cellInput} ${styles.cellNum}`}
                      type="number"
                      step="0.1"
                      value={dr[f]}
                      onChange={(e) => setField(r.den, f, e.target.value)}
                    />
                  );
                  return (
                    <tr key={r.den}>
                      <td className={styles.dayCell}>{r.den}.</td>
                      <td>
                        <input
                          className={styles.cellInput}
                          value={dr.cesta}
                          onChange={(e) => setField(r.den, 'cesta', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.cellInput}
                          list="kj-ucely"
                          value={dr.ucel}
                          onChange={(e) => setField(r.den, 'ucel', e.target.value)}
                        />
                      </td>
                      <td>{numInput('km_sluzebni')}</td>
                      <td>{numInput('km_soukrome')}</td>
                      <td className={`${styles.tacho} ${styles.right}`}>{fmtKm(r.pred)}</td>
                      <td className={`${styles.tacho} ${styles.right} ${styles.tachoStrong}`}>
                        {r.po !== null ? fmtKm(r.po) : '—'}
                      </td>
                      <td>{numInput('nat_benzin')}</td>
                      <td>{numInput('nat_nafta')}</td>
                      <td>{numInput('nat_olej')}</td>
                      <td>
                        <input
                          className={styles.cellInput}
                          value={dr.podpis}
                          onChange={(e) => setField(r.den, 'podpis', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.cellInput}
                          value={dr.pozn}
                          onChange={(e) => setField(r.den, 'pozn', e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Vehicle modal */}
      <Modal
        open={vehicleModal !== null}
        onClose={() => setVehicleModal(null)}
        title={vehicleModal === 'edit' ? 'Upravit vozidlo' : 'Nové vozidlo'}
        footer={
          <div className={styles.modalFooter}>
            {vehicleModal === 'edit' && (
              <Button variant="danger" size="sm" onClick={removeVehicle}>Smazat vozidlo</Button>
            )}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" onClick={() => setVehicleModal(null)}>Zrušit</Button>
            <Button variant="primary" size="sm" onClick={saveVehicle}>Uložit</Button>
          </div>
        }
      >
        <div className={styles.formGrid}>
          <Input
            label="Název" required placeholder="Passat Alltrack"
            value={vehicleForm.nazev}
            onChange={(e) => setVehicleForm((f) => ({ ...f, nazev: e.target.value }))}
          />
          <Input
            label="SPZ" placeholder="1AB 2345"
            value={vehicleForm.spz}
            onChange={(e) => setVehicleForm((f) => ({ ...f, spz: e.target.value }))}
          />
        </div>
        <Input
          label="Typ vozidla" placeholder="Volkswagen Passat Alltrack"
          value={vehicleForm.typ}
          onChange={(e) => setVehicleForm((f) => ({ ...f, typ: e.target.value }))}
        />
        <div className={styles.formGrid}>
          <Input
            label="Č. tankovací karty"
            value={vehicleForm.karta}
            onChange={(e) => setVehicleForm((f) => ({ ...f, karta: e.target.value }))}
          />
          <Input
            label="Spotřeba dle TP (l/100 km)" type="number" placeholder="6.5"
            value={vehicleForm.spotreba}
            onChange={(e) => setVehicleForm((f) => ({ ...f, spotreba: e.target.value }))}
          />
        </div>
        <Input
          label="Řidič (výchozí)" placeholder="Jméno řidiče"
          value={vehicleForm.ridic}
          onChange={(e) => setVehicleForm((f) => ({ ...f, ridic: e.target.value }))}
        />
      </Modal>

      {/* Add month modal */}
      <Modal
        open={addMonthOpen}
        onClose={() => setAddMonthOpen(false)}
        title="Přidat měsíc"
        footer={
          <div className={styles.modalFooter}>
            <span style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" onClick={() => setAddMonthOpen(false)}>Zrušit</Button>
            <Button variant="primary" size="sm" onClick={confirmAddMonth}>Vytvořit</Button>
          </div>
        }
      >
        <div className={styles.formGrid}>
          <Input
            label="Rok" type="number"
            value={amYear}
            onChange={(e) => setAmYear(Number(e.target.value))}
          />
          <label className={styles.selectField}>
            <span className={styles.selectFieldLabel}>Měsíc</span>
            <select
              className={styles.select}
              value={amMonth}
              onChange={(e) => setAmMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{MONTH_NAMES[m]}</option>
              ))}
            </select>
          </label>
        </div>
        <Input
          label="Počáteční stav tachometru (km)" type="number"
          placeholder="prázdné = navázat na předchozí měsíc"
          value={amPocatecni}
          onChange={(e) => setAmPocatecni(e.target.value)}
        />
      </Modal>
    </ListPage>
  );
}
